/**
 * Git Integration Hook
 * 提供 Git 仓库集成列表和检测功能
 */

import { useState, useCallback } from 'react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { GitIntegrationListItem, IntegrationCheckResult } from './types';

export function useGitIntegration() {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [integrations, setIntegrations] = useState<GitIntegrationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<IntegrationCheckResult | null>(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResults, setCheckResults] = useState<Map<string, { status: 'ok' | 'warning' | 'error'; checkedAt?: string }>>(new Map());
  const [fullCheckResults, setFullCheckResults] = useState<Map<string, IntegrationCheckResult>>(new Map());
  const [checkingHost, setCheckingHost] = useState<string | undefined>();

  // 获取集成列表
  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/git-integration/integrations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch integrations: ${response.status}`);
      }

      const data = await response.json();
      setIntegrations(data.integrations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi]);

  // 检测指定集成
  const checkIntegration = useCallback(async (host: string, forceRefresh = false) => {
    setCheckLoading(true);
    setCheckingHost(host);
    setCheckResult(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const url = `${baseUrl}/git-integration/check/${encodeURIComponent(host)}${forceRefresh ? '?refresh=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to check integration: ${response.status}`);
      }

      const result = await response.json();
      setCheckResult(result);

      // 缓存完整结果
      setFullCheckResults(prev => {
        const newMap = new Map(prev);
        newMap.set(host, result);
        return newMap;
      });

      // 更新状态摘要
      setCheckResults(prev => {
        const newMap = new Map(prev);
        newMap.set(host, {
          status: result.tokenStatus === 'valid' ? 'ok' :
                  result.tokenStatus === 'expiring_soon' ? 'warning' : 'error',
          checkedAt: result.checkedAt,
        });
        return newMap;
      });

      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCheckResults(prev => {
        const newMap = new Map(prev);
        newMap.set(host, { status: 'error' });
        return newMap;
      });
      return null;
    } finally {
      setCheckLoading(false);
      setCheckingHost(undefined);
    }
  }, [discoveryApi, identityApi]);

  // 获取缓存的完整检测结果
  const getCheckResult = useCallback((host: string): IntegrationCheckResult | undefined => {
    return fullCheckResults.get(host);
  }, [fullCheckResults]);

  return {
    integrations,
    loading,
    error,
    checkResult,
    checkLoading,
    checkResults,
    checkingHost,
    fetchIntegrations,
    checkIntegration,
    getCheckResult,
  };
}
